import { SubmitButton } from "@xola/ui-kit";
import { FormField, requiredNumberMoreThanZero, requiredString, SelectField } from "common";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { CouponCode, CouponCodeDuplicateOption, CouponType } from "types";
import { array, object } from "yup";
import { ScrollToFieldError } from "../../helpers/validationHelper";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";
import { ContentLayout } from "../Layout/ContentLayout";
import { CouponCodeInput } from "./CouponCodeInput";
import { CouponEditorProps } from "./CouponEditorProps";
import LimitFormField from "./LimitFormField";

const validationSchema = object({
    limitType: requiredString(),
    limit: requiredNumberMoreThanZero(),
    codes: array().min(1, "is required").required(),
});

const enum CodeLimitType {
    UNLIMITED = "unlimited",
    LIMITED = "limited",
}

interface FormValues {
    limitType: CodeLimitType;
    limit: number;
    codes: CouponCode[];
}

const initialValues = {
    limitType: CodeLimitType.LIMITED,
    limit: 100,
    codes: [],
};

export const CreateCouponCodesForm = ({ coupon, isSaving, onSubmit, onCancel }: CouponEditorProps) => {
    const [duplicateOption, setDuplicateOption] = useState<CouponCodeDuplicateOption | null>(null);
    const isVoucher = coupon.type === CouponType.VOUCHER;

    const { error, mutate: saveCoupon } = useMutation(async ({ limitType, limit, codes }: FormValues) => {
        return onSubmit({
            coupon: { ...coupon, limit: limitType === CodeLimitType.LIMITED && !isVoucher ? limit : null, codes },
            duplicate: duplicateOption,
        });
    });

    const handleSubmit = async (values: FormValues) => {
        saveCoupon(values);
    };

    return (
        <XolaFormik
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={validationSchema}
            initialValues={initialValues}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, setFieldValue, values, errors }) => (
                <ContentLayout isWide isDisabled={isSaving}>
                    <ContentLayout.Section
                        title="Codes"
                        description="Code that must be entered while making a booking to provide the discount you have created."
                    >
                        {isVoucher ? null : (
                            <div className="flex items-center space-x-4">
                                <FormField name="limitType" label="Code Type" className="flex-1">
                                    <SelectField>
                                        <option value={CodeLimitType.LIMITED}>Limited Use</option>
                                        <option value={CodeLimitType.UNLIMITED}>Unlimited Use</option>
                                    </SelectField>
                                </FormField>

                                {values.limitType === CodeLimitType.LIMITED ? <LimitFormField /> : null}
                            </div>
                        )}

                        <FormField
                            name="codes"
                            label="Codes"
                            tooltip={
                                isVoucher
                                    ? "One-time use codes expire after a single booking. Input multiple codes separated by a comma or a new line."
                                    : "Input multiple codes separated by a comma or a new line."
                            }
                        >
                            <CouponCodeInput
                                isError={!!errors.codes}
                                value={values.codes}
                                error={error}
                                duplicateOption={duplicateOption}
                                onChange={(codes) => setFieldValue("codes", codes)}
                                onDuplicateOptionChange={(duplicateOption) => setDuplicateOption(duplicateOption)}
                            />
                        </FormField>
                    </ContentLayout.Section>
                    <ContentLayout.Footer>
                        <SubmitButton type="button" isLoading={isSaving} onClick={handleSubmit}>
                            Save
                        </SubmitButton>

                        <SecondaryButton type="reset" onClick={onCancel}>
                            {coupon.id ? "Cancel" : "Back"}
                        </SecondaryButton>
                    </ContentLayout.Footer>

                    <ScrollToFieldError />
                </ContentLayout>
            )}
        </XolaFormik>
    );
};
